
import { useEffect, useState, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";

type AdminRole = 'super_admin' | 'admin' | 'finance' | 'dev' | 'onboarding';

interface MenuAccess {
  dashboard: boolean;
  products: boolean;
  settings: boolean;
  schools: boolean;
  users: boolean;
  staff: boolean;
  adminUsers: boolean;
  plans: boolean;
  subscriptions: boolean;
  discounts: boolean;
  pricingApi: boolean;
  paymentApi: boolean;
  subscriptionStatusApi: boolean;
}

export const useAdminPermissions = () => {
  const [adminRole, setAdminRole] = useState<AdminRole | null>(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState<Error | null>(null);
  const [fetchAttempted, setFetchAttempted] = useState(false);

  // Fetch the current admin user's role
  const fetchAdminRole = useCallback(async () => {
    if (fetchAttempted) return;
    
    try {
      setLoading(true);
      setFetchError(null);
      
      const { data: session } = await supabase.auth.getSession();
      if (!session.session) {
        setLoading(false);
        setFetchAttempted(true);
        return;
      }

      const { data: adminData, error } = await supabase
        .from('admin_users')
        .select('role')
        .eq('user_id', session.session.user.id)
        .maybeSingle();

      if (error) {
        console.error('Error fetching admin role:', error);
        setFetchError(error);
      } else {
        setAdminRole(adminData?.role as AdminRole || null);
      }
    } catch (error) {
      console.error('Error checking admin role:', error);
      setFetchError(error instanceof Error ? error : new Error('Unknown error fetching admin role'));
    } finally {
      setLoading(false);
      setFetchAttempted(true);
    }
  }, [fetchAttempted]);

  useEffect(() => {
    // Only fetch the role once on mount
    fetchAdminRole();
  }, [fetchAdminRole]);

  // Memoize the menuAccess to prevent unnecessary recalculations
  const menuAccess = useMemo((): MenuAccess => {
    // Default is no access
    const baseAccess: MenuAccess = {
      dashboard: false,
      products: false,
      settings: false,
      schools: false,
      users: false,
      staff: false,
      adminUsers: false,
      plans: false,
      subscriptions: false,
      discounts: false,
      pricingApi: false,
      paymentApi: false,
      subscriptionStatusApi: false,
    };

    // If no role found, return base access but always allow dashboard as a fallback
    if (!adminRole) {
      return { ...baseAccess, dashboard: true };
    }

    switch (adminRole) {
      case 'super_admin':
        // Super admin has access to everything
        return {
          dashboard: true,
          products: true,
          settings: true,
          schools: true,
          users: true,
          staff: true,
          adminUsers: true,
          plans: true,
          subscriptions: true,
          discounts: true,
          pricingApi: true,
          paymentApi: true,
          subscriptionStatusApi: true,
        };
      
      case 'admin':
        // Admin: All menu except Admin Users, Settings and API
        return {
          dashboard: true,
          products: true,
          settings: false,
          schools: true,
          users: true,
          staff: true,
          adminUsers: false,
          plans: true,
          subscriptions: true,
          discounts: true,
          pricingApi: false,
          paymentApi: false,
          subscriptionStatusApi: false,
        };
      
      case 'finance':
        // Finance: Dashboard, Schools, Product, Plans, Subscription, Discount
        return {
          dashboard: true,
          products: true,
          settings: false,
          schools: true,
          users: false,
          staff: false,
          adminUsers: false,
          plans: true,
          subscriptions: true,
          discounts: true,
          pricingApi: false,
          paymentApi: false,
          subscriptionStatusApi: false,
        };
      
      case 'dev':
        // Dev: Dashboard, Product, All API, Schools
        return {
          dashboard: true,
          products: true,
          settings: false,
          schools: true,
          users: false,
          staff: false,
          adminUsers: false,
          plans: false,
          subscriptions: false,
          discounts: false,
          pricingApi: true,
          paymentApi: true,
          subscriptionStatusApi: true,
        };
      
      case 'onboarding':
        // Onboarding: Dashboard, Schools, User
        return {
          dashboard: true,
          products: false,
          settings: false,
          schools: true,
          users: true,
          staff: false,
          adminUsers: false,
          plans: false,
          subscriptions: false,
          discounts: false,
          pricingApi: false,
          paymentApi: false,
          subscriptionStatusApi: false,
        };
      
      default:
        return { ...baseAccess, dashboard: true };
    }
  }, [adminRole]);

  return { 
    adminRole, 
    loading: loading && !fetchAttempted,
    error: fetchError,
    menuAccess
  };
};
